import clsx from "clsx";
import React from "react";
import {
  DialogProps,
  Button,
  Box,
  Link,
  Typography,
  Stack,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { BaseDialog } from "./BaseDialog";
import { BrandedAppLogoSwitchContent } from "../settings/branded-app/BrandedAppLogoSwitchContent";
import { COACH_BRANDED_APP_ROUTE } from "../../routes/routes";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    maxWidth: 600,
  },

  actions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },

  primaryButton: {
    width: "100%",
    fontWeight: "bold",
  },

  linkButton: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  dialogActions: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  dialogButton: {
    borderRadius: 4,
    fontSize: 16,
    fontWeight: "bold",
    flex: 1,
    height: theme.spacing(7),
  },

  cancelButton: {
    color: theme.palette.text.secondary,
    borderWidth: 2,
  },

  confirmButton: {
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

export interface CBAPreactivationDialogProps
  extends Omit<DialogProps, "title"> {
  title?: string;
  description?: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmButtonText?: string;
  disabled?: boolean;
}

export function CBAPreactivationDialog(props: CBAPreactivationDialogProps) {
  const {
    className,
    title,
    description,
    onCancel,
    onConfirm,
    confirmButtonText = "Activate",
    disabled,
    ...other
  } = props;
  const s = useStyles();
  const navigate = useNavigate();

  const handleLearnMore = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.open(
        process.env.REACT_APP_CLIENT_URL + COACH_BRANDED_APP_ROUTE,
        "_blank",
      );
    } else {
      navigate(COACH_BRANDED_APP_ROUTE, { replace: true });
    }
  }, [navigate]);

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title={title}
      onClose={onCancel}
      classes={{
        paper: s.paper,
      }}
      {...other}
    >
      <Stack gap={6}>
        <Typography color="secondary">
          Give your clients a premium, fully branded mobile app on iPhone and
          Android - delivered in 30 days after your application is approved.
          Email{" "}
          <Link className={s.linkButton} href="mailto:accounts@stridist.com">
            accounts@stridist.com
          </Link>{" "}
          and we'll add you to the priority app-building list.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <BrandedAppLogoSwitchContent />
        </Box>
        <Box>
          <Typography variant="h6">
            Pay only when the app is published
          </Typography>
          <Typography>
            You only start paying +$100/month (on top of your Stridist
            subscription) after your app is live in both the Apple App Store and
            Google Play, ready for your clients to download and use.
          </Typography>
        </Box>
      </Stack>
      <Box className={s.actions}>
        <Button
          href="mailto:accounts@stridist.com"
          variant="contained"
          color="primary"
          className={s.primaryButton}
        >
          Join priority list
        </Button>
        <Link
          className={s.linkButton}
          onClick={handleLearnMore}
          component="button"
          type="button"
        >
          Learn more
        </Link>
      </Box>
      {
        // TODO_ADDONS: Uncomment once CBA addon activation is possible
        /* <Box className={s.dialogActions}>
        <Button
          className={clsx(s.dialogButton, s.cancelButton)}
          variant="outlined"
          onClick={onCancel}
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button
          className={clsx(s.dialogButton, s.confirmButton)}
          variant="contained"
          onClick={onConfirm}
          disabled={disabled}
        >
          {confirmButtonText}
        </Button>
      </Box> */
      }
    </BaseDialog>
  );
}
